import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, ChevronUp, ChevronDown, Trash2, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import { backdropClose } from '../utils/backdropClose';
import {
  RECIPES, HORIZON_DAYS, WORKOUT_TYPES, METRIC_KINDS, METRIC_ORDER,
  fmtTarget, sentence,
  type MetricKind, type TargetMap, type WorkoutType,
} from '../app/member/plan/planShared';
import { picksForFocus, allExercises, type LibraryExercise } from '../app/member/plan/exerciseLibrary';

/* Trainer-side workout plan builder (work order B6). Same 3-step flow as the
   member app, plus assignment: the API stamps createdBy with the trainer's user
   id and honours allowMemberEdits, which decides whether the member may
   restructure the plan afterwards (logging actuals always stays theirs). */

interface Props {
  memberId: string;
  memberName: string;
  onClose: () => void;
}

interface PlanItem {
  id: string;
  exerciseName: string;
  workoutType: string;
  position: number;
  target: TargetMap;
  actual: TargetMap | null;
  status: string;
  source: string;
}

interface PlanDay {
  id: string;
  date: string;
  focus: string;
  isRest: boolean;
  position: number;
  startTime: string | null;
  endTime: string | null;
  items: PlanItem[];
}

interface Plan {
  id: string;
  recipeKey: string;
  startsOn: string;
  horizonDays: number;
  status: string;
  createdBy: string;
  allowMemberEdits: boolean;
  days: PlanDay[];
}

interface DraftItem {
  key: string;
  exerciseName: string;
  workoutType: WorkoutType;
  target: TargetMap;
}

interface DraftDay {
  date: string;
  focus: string;
  isRest: boolean;
  items: DraftItem[];
}

let keySeq = 0;
const nextKey = () => `t${++keySeq}`;

const addDays = (ymd: string, n: number) => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const tomorrow = () => addDays(new Date().toISOString().slice(0, 10), 1);

const fmtDay = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const toDraftItem = (e: LibraryExercise): DraftItem => ({
  key: nextKey(), exerciseName: e.name, workoutType: e.workoutType, target: { ...e.target },
});

async function authFetch(path: string, init: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed (${res.status})`) as Error & { code?: string };
    err.code = body?.code;
    throw err;
  }
  return body;
}

export default function MemberWorkoutPlanPanel({ memberId, memberName, onClose }: Props) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false); // member disabled progress sharing

  const [building, setBuilding] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [recipeKey, setRecipeKey] = useState('rot');
  const [horizon, setHorizon] = useState(7);
  const [startsOn, setStartsOn] = useState(tomorrow);
  const [days, setDays] = useState<DraftDay[]>([]);
  const [dayIndex, setDayIndex] = useState(0);
  const [allowMemberEdits, setAllowMemberEdits] = useState(false);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setBlocked(false);
    try {
      const r = await authFetch(`/api/members/${memberId}/plan/active`);
      setPlan(r.data ?? null);
    } catch (e) {
      const err = e as Error & { code?: string };
      if (err.code === 'PROGRESS_SHARING_DISABLED') {
        setBlocked(true);
      } else {
        setError(err.message || 'Could not load this member’s plan');
      }
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => { load(); }, [load]);

  const recipe = RECIPES.find(r => r.key === recipeKey)!;

  const startBuilder = () => {
    setStep(1);
    setBuilding(true);
  };

  const buildDays = () => {
    setDays(Array.from({ length: horizon }, (_, i) => {
      const focus = recipe.days[i % 7];
      const isRest = focus === 'Rest';
      return {
        date: addDays(startsOn, i),
        focus,
        isRest,
        items: isRest ? [] : picksForFocus(focus).map(toDraftItem),
      };
    }));
    setDayIndex(0);
    setStep(2);
  };

  /** Reorder focuses across fixed dates — the dates themselves never move. */
  const moveFocus = (from: number, to: number) => {
    if (to < 0 || to >= days.length) return;
    setDays(prev => {
      const focuses = prev.map(d => ({ focus: d.focus, isRest: d.isRest, items: d.items }));
      const [moved] = focuses.splice(from, 1);
      focuses.splice(to, 0, moved);
      return prev.map((d, i) => ({ ...d, ...focuses[i] }));
    });
  };

  const setItemMetric = (di: number, key: string, k: MetricKind, value: number | null) =>
    setDays(prev => prev.map((d, i) => {
      if (i !== di) return d;
      return {
        ...d,
        items: d.items.map(it => {
          if (it.key !== key) return it;
          const target = { ...it.target };
          if (value === null) delete target[k];
          else target[k] = Number(value.toFixed(2));
          return { ...it, target };
        }),
      };
    }));

  const removeItem = (di: number, key: string) =>
    setDays(prev => prev.map((d, i) => (i === di ? { ...d, items: d.items.filter(it => it.key !== key) } : d)));

  const addItem = (di: number, e: LibraryExercise) =>
    setDays(prev => prev.map((d, i) => (i === di ? { ...d, items: [...d.items, toDraftItem(e)] } : d)));

  const applyToAllDays = (di: number) => {
    const source = days[di];
    setDays(prev => prev.map(d =>
      d.isRest || d.date === source.date ? d : { ...d, items: source.items.map(it => ({ ...it, key: nextKey() })) }
    ));
    toast.success('Applied to every training day');
  };

  const assign = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await authFetch(`/api/members/${memberId}/plan`, {
        method: 'POST',
        body: JSON.stringify({
          recipeKey,
          startsOn: days[0].date,
          horizonDays: horizon,
          allowMemberEdits,
          days: days.map(d => ({
            date: d.date,
            focus: d.focus,
            isRest: d.isRest,
            items: d.items.map(it => ({
              exerciseName: it.exerciseName,
              workoutType: it.workoutType,
              target: it.target,
            })),
          })),
        }),
      });
      toast.success(`Plan assigned to ${memberName}`);
      setBuilding(false);
      await load();
    } catch (e) {
      toast.error((e as Error).message || 'Could not assign the plan');
    } finally {
      setSaving(false);
    }
  };

  const toggleMemberEdits = async (value: boolean) => {
    if (!plan) return;
    const previous = plan.allowMemberEdits;
    setPlan({ ...plan, allowMemberEdits: value });
    try {
      await authFetch(`/api/members/${memberId}/plan/${plan.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ allowMemberEdits: value }),
      });
    } catch (e) {
      setPlan(p => (p ? { ...p, allowMemberEdits: previous } : p));
      toast.error((e as Error).message || 'Could not update the plan');
    }
  };

  const archivePlan = async () => {
    if (!plan) return;
    try {
      await authFetch(`/api/members/${memberId}/plan/${plan.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'archived' }),
      });
      toast.success('Plan archived');
      await load();
    } catch (e) {
      toast.error((e as Error).message || 'Could not archive the plan');
    }
  };

  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pool = allExercises();
    return (q ? pool.filter(e => e.name.toLowerCase().includes(q)) : pool).slice(0, 12);
  }, [search]);

  const totals = useMemo(() => ({
    workouts: days.filter(d => !d.isRest).length,
    exercises: days.reduce((n, d) => n + d.items.length, 0),
  }), [days]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      {...backdropClose(onClose)}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Workout plan</h2>
            <p className="text-sm text-gray-500">{memberName}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {loading ? (
            <div className="space-y-3">
              {[0, 1, 2].map(i => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
            </div>
          ) : blocked ? (
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl text-sm text-amber-800">
              <strong>Progress sharing is off.</strong> {memberName} has turned off sharing their progress with
              coaches, so their plan and logs are not visible here. They can turn it back on in the member app
              under Profile → Notifications &amp; privacy.
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={load} className="px-3 py-1.5 rounded-lg bg-white border border-red-200 text-xs font-semibold">
                Retry
              </button>
            </div>
          ) : building ? (
            <>
              {/* ── Step 1 · shape ─────────────────────────────────────── */}
              {step === 1 && (
                <>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-sm font-semibold text-gray-700">Starts on</label>
                    <input
                      type="date"
                      value={startsOn}
                      onChange={e => setStartsOn(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    />
                    <div className="flex gap-2">
                      {HORIZON_DAYS.map(h => (
                        <button
                          key={h}
                          onClick={() => setHorizon(h)}
                          className={`px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                            horizon === h ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                          }`}
                        >
                          {h} days
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    {RECIPES.map(r => (
                      <button
                        key={r.key}
                        onClick={() => setRecipeKey(r.key)}
                        className={`text-left p-4 rounded-xl border transition-colors ${
                          r.key === recipeKey ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-900 text-sm">{r.name}</span>
                          {r.tag && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-lime-50 text-lime-700 border border-lime-100">
                              {r.tag}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{r.sub}</div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {r.days.map((f, i) => (
                            <span
                              key={i}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                f === 'Rest' ? 'bg-gray-100 text-gray-500' : 'bg-amber-50 text-amber-700'
                              }`}
                            >
                              {f}
                            </span>
                          ))}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* ── Step 2 · arrange (dates fixed) ─────────────────────── */}
              {step === 2 && (
                <>
                  <p className="text-xs text-gray-500">
                    Dates stay put — reordering moves the focus (and its exercises) between days.
                  </p>
                  <div className="space-y-2">
                    {days.map((d, i) => (
                      <div key={d.date} className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl">
                        <div className="w-24 text-xs font-semibold text-gray-500">{fmtDay(d.date)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-gray-900 truncate">{d.focus}</div>
                          <div className="text-xs text-gray-500">
                            {d.isRest ? 'Rest day' : `${d.items.length} exercise${d.items.length === 1 ? '' : 's'}`}
                          </div>
                        </div>
                        <button
                          onClick={() => moveFocus(i, i - 1)}
                          disabled={i === 0}
                          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
                          aria-label="Move earlier"
                        >
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => moveFocus(i, i + 1)}
                          disabled={i === days.length - 1}
                          className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30"
                          aria-label="Move later"
                        >
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => { setDayIndex(i); setStep(3); }}
                          className="px-3 py-1.5 rounded-lg bg-gray-100 text-xs font-semibold text-gray-700"
                        >
                          Exercises
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* ── Step 3 · exercises + targets ───────────────────────── */}
              {step === 3 && days[dayIndex] && (
                <>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {days.map((d, i) => (
                      <button
                        key={d.date}
                        onClick={() => setDayIndex(i)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border ${
                          i === dayIndex ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'
                        }`}
                      >
                        {fmtDay(d.date)} · {d.focus}
                      </button>
                    ))}
                  </div>

                  {days[dayIndex].isRest ? (
                    <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl">
                      Rest day — nothing to pick.
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {days[dayIndex].items.map(it => {
                          const kinds = (WORKOUT_TYPES[it.workoutType]?.metricKeys ?? [])
                            .slice()
                            .sort((a, b) => METRIC_ORDER.indexOf(a) - METRIC_ORDER.indexOf(b));
                          return (
                            <div key={it.key} className="p-3 border border-gray-200 rounded-xl">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="font-semibold text-sm text-gray-900">{it.exerciseName}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">{sentence(it.exerciseName, it.target)}</div>
                                </div>
                                <button
                                  onClick={() => removeItem(dayIndex, it.key)}
                                  className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                                  aria-label={`Remove ${it.exerciseName}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <div className="flex flex-wrap gap-2 mt-3">
                                {kinds.map(k => {
                                  const def = METRIC_KINDS[k];
                                  const on = it.target[k] != null;
                                  return (
                                    <div
                                      key={k}
                                      className={`flex items-center gap-2 px-2 py-1 rounded-lg border text-xs ${
                                        on ? 'border-amber-200 bg-amber-50' : 'border-dashed border-gray-200 text-gray-400'
                                      }`}
                                    >
                                      <span className="font-semibold">{def.label}</span>
                                      <input
                                        type="number"
                                        step={def.step}
                                        min={0}
                                        value={on ? it.target[k] : ''}
                                        placeholder="—"
                                        onChange={e => {
                                          const v = e.target.value;
                                          setItemMetric(dayIndex, it.key, k, v === '' ? null : Number(v));
                                        }}
                                        className="w-16 px-1 py-0.5 rounded border border-gray-200 text-center tabular-nums"
                                      />
                                      <span className="text-gray-400">{def.unit}</span>
                                      {on && (
                                        <button
                                          onClick={() => setItemMetric(dayIndex, it.key, k, null)}
                                          className="text-gray-400 hover:text-red-500"
                                          title="Leave this metric open"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div className="border border-gray-200 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Search className="w-4 h-4 text-gray-400" />
                          <input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search exercises to add"
                            className="flex-1 text-sm outline-none"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {searchResults.map(e => (
                            <button
                              key={e.name}
                              onClick={() => addItem(dayIndex, e)}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-50 border border-gray-200 text-xs font-semibold text-gray-700 hover:border-gray-300"
                            >
                              <Plus className="w-3 h-3" /> {e.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => applyToAllDays(dayIndex)}
                        className="px-3 py-2 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600"
                      >
                        Apply this day to all training days
                      </button>
                    </>
                  )}
                </>
              )}
            </>
          ) : plan ? (
            <>
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {RECIPES.find(r => r.key === plan.recipeKey)?.name ?? plan.recipeKey}
                    </div>
                    <div className="text-xs text-gray-500">
                      {plan.horizonDays} days from {fmtDay(plan.startsOn)} ·{' '}
                      {plan.createdBy === 'member' ? 'member-authored' : 'trainer-assigned'} · {plan.status}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                    <input
                      type="checkbox"
                      checked={plan.allowMemberEdits}
                      onChange={e => toggleMemberEdits(e.target.checked)}
                      className="rounded"
                    />
                    Allow member edits
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                {plan.days.map(d => (
                  <div key={d.id} className="p-3 border border-gray-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900">{fmtDay(d.date)} · {d.focus}</div>
                      <div className="text-xs text-gray-500">
                        {d.isRest ? 'Rest' : `${d.items.length} exercise${d.items.length === 1 ? '' : 's'}`}
                        {d.items.some(i => i.status === 'done') &&
                          ` · ${d.items.filter(i => i.status === 'done').length} logged`}
                      </div>
                    </div>
                    {d.items.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {d.items.map(i => (
                          <div key={i.id} className="flex items-center justify-between text-xs">
                            <span className={i.status === 'done' ? 'text-gray-400 line-through' : 'text-gray-700'}>
                              {i.exerciseName}
                            </span>
                            <span className="text-gray-500 tabular-nums">{fmtTarget(i.actual ?? i.target)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="p-8 text-center border border-dashed border-gray-200 rounded-xl">
              <p className="font-semibold text-gray-900">No active plan</p>
              <p className="text-sm text-gray-500 mt-1">Build one from a recipe and assign it in a minute.</p>
            </div>
          )}
        </div>

        {!loading && !blocked && !error && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
            {building ? (
              <>
                <button
                  onClick={() => (step === 1 ? setBuilding(false) : setStep(step === 3 ? 2 : 1))}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600"
                >
                  Back
                </button>
                <div className="flex items-center gap-3 flex-wrap">
                  {step === 3 && (
                    <>
                      <span className="text-xs text-gray-500">
                        {totals.workouts} workouts · {totals.exercises} exercises
                      </span>
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700">
                        <input
                          type="checkbox"
                          checked={allowMemberEdits}
                          onChange={e => setAllowMemberEdits(e.target.checked)}
                          className="rounded"
                        />
                        Allow member edits
                      </label>
                    </>
                  )}
                  <button
                    onClick={step === 1 ? buildDays : step === 2 ? () => setStep(3) : assign}
                    disabled={saving}
                    className="px-5 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold disabled:opacity-50"
                  >
                    {step === 1 ? 'Continue' : step === 2 ? 'Pick exercises' : saving ? 'Assigning…' : 'Assign to member'}
                  </button>
                </div>
              </>
            ) : (
              <>
                {plan && plan.status === 'active' ? (
                  <button
                    onClick={archivePlan}
                    className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600"
                  >
                    Archive plan
                  </button>
                ) : <span />}
                <button
                  onClick={startBuilder}
                  className="px-5 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold"
                >
                  {plan ? 'Replace with a new plan' : 'Build a plan'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
